import { useRef, useEffect, useCallback, type ReactNode, type CSSProperties } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

interface StickyListProps<T> {
  items: T[];
  renderRow: (item: T, index: number) => ReactNode;
  estimatedRowHeight?: number;
  overscan?: number;
  style?: CSSProperties;
}

const SCROLL_BOTTOM_THRESHOLD = 50;
const DEFAULT_ESTIMATED_ROW_HEIGHT = 24;
const DEFAULT_OVERSCAN = 10;

function StickyList<T>({
  items,
  renderRow,
  estimatedRowHeight = DEFAULT_ESTIMATED_ROW_HEIGHT,
  overscan = DEFAULT_OVERSCAN,
  style
}: StickyListProps<T>) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isStuckToBottomRef = useRef(true);

  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan
  });
  const totalSize = virtualizer.getTotalSize();

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isStuckToBottomRef.current = distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD;
  }, []);

  useEffect(() => {
    if (!isStuckToBottomRef.current || items.length === 0) return;

    const el = scrollContainerRef.current;
    if (!el) return;

    const previousOverflowY = el.style.overflowY;
    el.style.overflowY = 'hidden';
    virtualizer.scrollToIndex(items.length - 1, { align: 'end' });

    const frameId = requestAnimationFrame(() => {
      el.style.overflowY = previousOverflowY || 'scroll';
    });

    return () => {
      cancelAnimationFrame(frameId);
      el.style.overflowY = previousOverflowY || 'scroll';
    };
  }, [items.length, totalSize, virtualizer]);

  return (
    <div
      ref={scrollContainerRef}
      onScroll={handleScroll}
      style={{
        ...style,
        overflowAnchor: 'none',
        scrollbarGutter: 'stable',
        overflowY: 'scroll'
      }}
    >
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: '100%',
          position: 'relative'
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => (
          <div
            key={virtualItem.index}
            ref={virtualizer.measureElement}
            data-index={virtualItem.index}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualItem.start}px)`
            }}
          >
            {renderRow(items[virtualItem.index], virtualItem.index)}
          </div>
        ))}
      </div>
    </div>
  );
}

export default StickyList;
